import React from 'react';
import { useGlobal } from '../global/GlobalContext';
import { FiTrash2, FiShoppingBag } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import SimpleLayout from '../layout/SimpleLayout';
import DistributorQuantityPopup from '../components/DistributorQuantityPopup';
import SizeSelectionPopup from '../components/SizeSelectionPopup';
import { useWishlistAddToCart } from '../hooks/useWishlistAddToCart';

const Wishlist = () => {
  const { wishlist, wishlistLoading } = useGlobal();
  const navigate = useNavigate();

  const {
    addingId,
    removingId,
    currentItem,
    isSizePopupOpen,
    sizeOptions,
    selectedVariation,
    setSelectedVariation,
    closeSizePopup,
    confirmSize,
    isQtyPopupOpen,
    selectedQuantity,
    setSelectedQuantity,
    popupQuantities,
    getItemPackQty,
    closeQtyPopup,
    confirmQuantity,
    handleAddToCartClick,
    removeFromWishlist,
  } = useWishlistAddToCart();

  const renderCard = (item, product) => (
    <div key={item.id} className="wishlist-item rounded-2xl border border-gray-100 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
      <Link to={`/p/${product.slug}`} className="block rounded-xl overflow-hidden bg-gray-50 aspect-[4/5]">
        <img
          src={product.primary_img}
          alt={product.prod_name}
          loading="lazy"
          className="w-full h-full object-cover object-top"
        />
      </Link>
      <h3 className="text-sm font-semibold text-gray-900 mt-3 line-clamp-2">{product.prod_name}</h3>
      <div className="flex items-center gap-2 mt-1.5">
        <p className="text-gray-900 text-sm font-bold">₹{product.sale_price}</p>
        {product.regular_price > product.sale_price && (
          <p className="text-gray-400 line-through text-xs">₹{product.regular_price}</p>
        )}
      </div>

      <div className="flex justify-between items-center border-t border-gray-100 mt-3 pt-3">
        <button
          onClick={() => removeFromWishlist(item.id)}
          disabled={removingId === item.id}
          aria-label="Remove from wishlist"
          className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
        >
          {removingId === item.id ? (
            <span className="w-3.5 h-3.5 border-2 border-red-300 border-t-red-500 rounded-full animate-spin" />
          ) : (
            <FiTrash2 size={15} />
          )}
        </button>
        <button
          onClick={() => handleAddToCartClick(item)}
          disabled={addingId === item.id}
          className="inline-flex items-center gap-1.5 text-white bg-brand-600 hover:bg-brand-700 text-xs font-semibold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50"
        >
          <FiShoppingBag size={13} />
          {addingId === item.id ? 'Adding...' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );

  const skeletonGrid = (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6 animate-pulse">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4">
          <div className="rounded-xl bg-gray-200 aspect-[4/5]" />
          <div className="h-4 w-3/4 bg-gray-200 rounded mt-3" />
          <div className="h-4 w-1/2 bg-gray-200 rounded mt-2" />
          <div className="flex justify-between items-center border-t border-gray-100 mt-3 pt-3">
            <div className="w-9 h-9 rounded-full bg-gray-200" />
            <div className="h-8 w-24 bg-gray-200 rounded-xl" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <SimpleLayout ProductsCount={`(${wishlist.length} ${wishlist.length > 1 ? 'items' : 'item'})`} title='My Wishlist' link='javascript:;'>
      <div className="wishlist-page bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-2.5 mb-6">
            <FiShoppingBag className="text-brand-600" size={20} />
            <h2 className="text-xl font-bold text-gray-900">
              My Wishlist <span className="text-gray-400 font-medium">({wishlist.length} {wishlist.length !== 1 ? 'Items' : 'Item'})</span>
            </h2>
          </div>

          {wishlistLoading ? (
            skeletonGrid
          ) : wishlist.length > 0 ? (
            <div className="wishlist-items grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {wishlist.map((item) => renderCard(item, item.product || item))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-16">
              <svg
                width="100"
                height="100"
                viewBox="0 0 24 24"
                fill="none"
                className="mb-4 text-gray-300"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.62 20.29L12 20.77L11.38 20.29C6.21 16.28 2.5 13.06 2.5 9.14C2.5 6.34 4.68 4.25 7.25 4.25C8.89 4.25 10.46 5.14 11.25 6.42C12.04 5.14 13.61 4.25 15.25 4.25C17.82 4.25 20 6.34 20 9.14C20 13.06 16.29 16.28 11.12 20.29Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <p className="text-center text-gray-900 font-bold text-base mt-2">
                Your wishlist is feeling a little lonely 💔
              </p>
              <p className="text-center text-gray-400 mt-2 text-sm max-w-xs leading-5">
                But that's okay! Tap that ❤️ on your favorite items and build your dream list.
                Start discovering styles made just for you!
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-6 px-8 py-3 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                Start Exploring
              </button>
            </div>
          )}
        </div>
      </div>

      <SizeSelectionPopup
        open={isSizePopupOpen}
        sizeOptions={sizeOptions}
        selectedVariation={selectedVariation}
        onSelect={setSelectedVariation}
        onClose={closeSizePopup}
        onConfirm={confirmSize}
      />

      <DistributorQuantityPopup
        open={isQtyPopupOpen}
        packQty={getItemPackQty(currentItem, selectedVariation)}
        quantities={popupQuantities}
        selectedQuantity={selectedQuantity}
        onSelect={setSelectedQuantity}
        onClose={closeQtyPopup}
        onConfirm={confirmQuantity}
      />
    </SimpleLayout>
  );
};

export default Wishlist;
